import { getStoreId } from "$/hooks/use-store-information.ts"
import bikeConditionQueries from "$/lib/queries/bike-condition-queries"
import jobQueries from "$/lib/queries/job-queries"
import receiptQueries from "$/lib/queries/receipt-queries.ts"
import storeQueries from "$/lib/queries/store-queries.ts"
import workOrderQueries from "$/lib/queries/work-order-queries.ts"
import { JobStatus, JobStatusUtils } from "@repo/common"
import { createFileRoute, stripSearchParams } from "@tanstack/react-router"
import { z } from "zod"
import { ensureJobStoreSelected } from "../../-utils/ensure-job-store-selected"

const searchSchema = z.object({
  customerName: z.string().catch("").default(""),
  jobStatus: z
    .union([z.nativeEnum(JobStatus), z.literal("")])
    .catch("")
    .default(""),
  // Preserved search parameters for breadcrumb navigation
  returnView: z.string().catch("list").default("list"),
  returnPage: z.number().catch(1).default(1),
  returnStartDate: z.string().catch("").default(""),
  returnEndDate: z.string().catch("").default(""),
  returnStatus: z.array(z.string()).catch([]).default([]),
  returnTagSearchQuery: z.string().catch("").default(""),
  returnFrom: z
    .enum(["overview", "calendar"])
    .catch("calendar")
    .default("calendar"),
})

const paramsSchema = z.object({
  jobId: z.guid(),
})

export const Route = createFileRoute("/(app)/_auth/jobs/$jobId")({
  params: {
    parse: (params) => paramsSchema.parse(params),
    stringify: ({ jobId }) => ({ jobId: `${jobId}` }),
  },
  search: {
    middlewares: [
      stripSearchParams({
        customerName: "",
        jobStatus: "",
        returnView: "",
        returnPage: 1,
        returnStartDate: "",
        returnEndDate: "",
        returnStatus: [],
        returnTagSearchQuery: "",
        returnFrom: "calendar",
      }),
    ],
  },
  validateSearch: searchSchema,
  beforeLoad: ({ context: { queryClient }, params, preload }) =>
    ensureJobStoreSelected(queryClient, params.jobId, preload),
  loaderDeps: ({ search }) => ({
    customerName: search.customerName,
    jobStatus: search.jobStatus,
    returnView: search.returnView,
    returnPage: search.returnPage,
    returnStartDate: search.returnStartDate,
    returnEndDate: search.returnEndDate,
    returnStatus: search.returnStatus,
    returnTagSearchQuery: search.returnTagSearchQuery,
    returnFrom: search.returnFrom,
  }),
  loader: async ({ context: { queryClient }, params, deps }) => {
    const { jobStatus } = deps
    const storeId = await getStoreId()
    const job = await queryClient.fetchQuery(jobQueries.detail(params.jobId))

    // Fetch store for breadcrumb
    const store = storeId
      ? await queryClient.fetchQuery(storeQueries.detail(storeId))
      : null

    if (jobStatus) {
      if (JobStatusUtils.isStatusAtOrPast(jobStatus, JobStatus.COMPLETED)) {
        await queryClient.prefetchQuery(
          bikeConditionQueries.detail(params.jobId),
        )
      }

      if (
        JobStatusUtils.isStatusAtOrPast(
          jobStatus,
          JobStatus.SERVICE_PROPOSAL_ACCEPTED,
        )
      ) {
        await queryClient.prefetchQuery(workOrderQueries.detail(params.jobId))
        await queryClient.prefetchQuery(receiptQueries.detail(params.jobId))
      }
    }

    // Construct the return URL with preserved search parameters
    const searchParams = new URLSearchParams()
    if (deps.returnView) searchParams.set("view", deps.returnView)
    if (deps.returnPage > 1)
      searchParams.set("page", deps.returnPage.toString())
    if (deps.returnStartDate)
      searchParams.set("startDate", deps.returnStartDate)
    if (deps.returnEndDate) searchParams.set("endDate", deps.returnEndDate)
    if (deps.returnStatus && deps.returnStatus.length > 0) {
      for (const status of deps.returnStatus) {
        searchParams.append("status", status)
      }
    }
    if (deps.returnTagSearchQuery)
      searchParams.set("tagSearchQuery", deps.returnTagSearchQuery)

    const returnUrl = searchParams.toString()
      ? `/calendar?${searchParams.toString()}`
      : "/calendar"

    const customerName = job.customer.lastName
      ? `${job.customer.firstName} ${job.customer.lastName}`
      : job.customer.firstName

    const storeName = store?.name ?? ""

    // If job is connected to an event, use event as the parent breadcrumb
    if (job.serviceEvent) {
      return {
        crumbs: [`Event - ${storeName}`, job.serviceEvent.title, customerName],
        froms: ["/event", `/event/${job.serviceEvent.id}`, undefined],
      }
    }

    if (deps.returnFrom === "overview") {
      return {
        crumbs: ["alfred.jobs.detail.crumbs.overview", customerName],
        froms: ["/", undefined],
      }
    }

    return {
      crumbs: ["alfred.jobs.detail.crumbs.calendar", customerName],
      froms: [returnUrl, undefined],
    }
  },
})
